import { LanguageCode, toLanguageCode } from '@/ifaces/language';
import { i18n } from '@lingui/core';
import type { CSSProperties } from 'react';
import * as en from './locales/en.po';

/**
 * Initialize i18n with English messages.
 * This is called on application startup to set the default language without the async warning.
 */
export function initializeI18n() {
  i18n.loadAndActivate({ locale: LanguageCode.English, messages: en.messages });
}

/**
 * Load messages for requested locale and activate it.
 * This function isn't part of the LinguiJS library because there are
 * many ways how to load messages — from REST API, from file, from cache, etc.
 */
export async function loadAndActivateI18n(locale: LanguageCode) {
  const dict = await import(`./locales/${locale}.po`);
  i18n.loadAndActivate({ locale, messages: dict.messages });
}

// Check if the locale is a right-to-left language
export function isRTL(locale: LanguageCode | string) {
  const code = toLanguageCode(locale);
  return ['ar', 'he', 'fa', 'ur'].includes(code);
}

// Apply HTML lang and dir attributes based on the locale
export function applyHtmlLangDir(locale: LanguageCode | string) {
  const code = toLanguageCode(locale);
  const html = document.documentElement;
  html.setAttribute('lang', code);
  html.setAttribute('dir', isRTL(code) ? 'rtl' : 'ltr');
}

// Directional arrow should be flipped for RTL languages
export function getLanguageCssTransform(locale: LanguageCode | string): CSSProperties {
  const code = toLanguageCode(locale);
  const transform = isRTL(code) ? 'scaleX(-1)' : 'scaleX(1)';
  return { transform };
}
